import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { ArrowLeft, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { workerApiService, Worker, WorkerCreateRequest } from '../services/workerApi';
import { branchApiService, Branch } from '../services/branchApi';
import { SHIFTS } from '../utils/constants';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { userApiService } from '../services/userApi';
import type { User } from '../types/user';
import { useBranch } from '../contexts/BranchContext';
import { departmentApiService, Department } from '../services/departmentApi';
import { workAreaApiService, WorkArea } from '../services/workAreaApi';

const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

export function WorkerInformationForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedBranch } = useBranch();
  const [current, setCurrent] = useState<Worker | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [workAreas, setWorkAreas] = useState<WorkArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [shifts, setShifts] = useState<string[]>([...SHIFTS]);
  const [skillLevels, setSkillLevels] = useState<string[]>([...SKILL_LEVELS]);
  const [isAddShiftOpen, setIsAddShiftOpen] = useState(false);
  const [isAddSkillOpen, setIsAddSkillOpen] = useState(false);
  const [isAddDepartmentOpen, setIsAddDepartmentOpen] = useState(false);
  const [newShift, setNewShift] = useState('');
  const [newSkill, setNewSkill] = useState('');
  const [newDepartment, setNewDepartment] = useState('');

  const [form, setForm] = useState<WorkerCreateRequest>({
    workerCode: '',
    name: '',
    address: '',
    dateOfJoining: '',
    contactNumber: '',
    emergencyContactNumber: '',
    aadhar: '',
    branchId: selectedBranch || undefined,
    department: '',
    workArea: '',
    workAreaId: undefined,
    departmentId: undefined,
    shiftId: undefined,
    reportingManager: '',
    shift: '',
    skillLevel: '',
    isActive: true,
  });

  useEffect(() => {
    if (id && window.location.pathname.includes('/view')) setViewMode(true);
    if (id && window.location.pathname.includes('/edit')) setViewMode(false);
  }, [id]);

  useEffect(() => {
    const init = async () => {
      try {
        const [branchList, usersResp, deptList, workAreaList] = await Promise.all([
          branchApiService.getBranches(),
          userApiService.getUsers(1, 100, selectedBranch ?? undefined),
          selectedBranch ? departmentApiService.getActiveByBranch(selectedBranch) : Promise.resolve([]),
          selectedBranch ? workAreaApiService.getActiveByBranch(selectedBranch) : Promise.resolve([])
        ]);
        setBranches(branchList);
        setUsers(usersResp?.data || []);
        setDepartments(deptList as Department[]);
        setWorkAreas(workAreaList as WorkArea[]);

        if (id) {
          const w = await workerApiService.getWorkerById(id);
          setCurrent(w);
          setForm({
            workerCode: w.workerCode || '',
            name: w.name,
            address: w.address || w.workerAddress || '',
            dateOfJoining: w.dateOfJoining?.slice(0, 10) || '',
            contactNumber: w.contactNumber,
            emergencyContactNumber: w.emergencyContactNumber || '',
            aadhar: w.aadhar || w.workerAadhar || '',
            branchId: w.branch?.id ?? w.branchId,
            department: w.department || '',
            departmentId: w.departmentId ?? undefined,
            workArea: w.workArea || '',
            workAreaId: w.workAreaId ?? undefined,
            reportingManager: w.reportingManager || '',
            reportingManagerUserId: (w as any).reportingManagerUserId, // optional backend field
            shift: w.shift || '',
            shiftId: w.shiftId ?? undefined,
            skillLevel: w.skillLevel || '',
            isActive: w.isActive ?? true,
          });
        }
        // create: workerCode left empty; do not call generate-code API
      } catch (e) {
        // already toasted in services
      }
    };
    init();
  }, [id, selectedBranch]);

  const handleChange = (key: keyof WorkerCreateRequest, value: string | number | boolean | undefined) => {
    setForm(prev => ({ ...prev, [key]: value } as WorkerCreateRequest));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.dateOfJoining || !form.contactNumber) {
      toast.error('Please fill in Name, Date of Joining and Contact No');
      return;
    }
    setLoading(true);
    try {
      if (current && current.id) {
        await workerApiService.updateWorker(String(current.id), { ...form, id: current.id });
        toast.success('Worker updated');
      } else {
        await workerApiService.createWorker(form);
        toast.success('Worker created');
      }
      navigate('/workers');
    } catch (e) {
      // errors handled
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate('/workers');

  return (
    <Card className="w-full border-0 shadow-lg bg-white/70 backdrop-blur-sm">
      <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-white hover:bg-primary-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <CardTitle className="text-xl">
              {viewMode ? 'View Worker' : current ? 'Edit Worker' : 'Add New Worker'}
            </CardTitle>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        <form onSubmit={handleSave} className="space-y-8">
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                Basic Information
              </h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter worker name"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Enter address"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Date of Joining <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="date"
                  value={form.dateOfJoining}
                  onChange={(e) => handleChange('dateOfJoining', e.target.value)}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Contact No <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={form.contactNumber}
                  onChange={(e) => handleChange('contactNumber', e.target.value)}
                  placeholder="Enter contact number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Emergency Contact No</Label>
                <Input
                  value={form.emergencyContactNumber}
                  onChange={(e) => handleChange('emergencyContactNumber', e.target.value)}
                  placeholder="Enter emergency contact number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                />
              </div>
              <div className="space-y-2">
                <Label>Aadhar</Label>
                <Input
                  value={form.aadhar}
                  onChange={(e) => handleChange('aadhar', e.target.value)}
                  placeholder="Enter Aadhar number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                />
              </div>
              {current && (
                <div className="space-y-2 flex items-center gap-3">
                  <Label htmlFor="worker-active" className="cursor-pointer">Active</Label>
                  <Switch
                    id="worker-active"
                    checked={form.isActive ?? true}
                    onCheckedChange={(checked) => handleChange('isActive', checked)}
                    disabled={viewMode}
                    className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-red-500"
                  />
                  <span className="text-sm text-muted-foreground">
                    {viewMode ? (form.isActive ? 'Active' : 'Inactive') : (form.isActive ? 'Worker is active' : 'Mark as inactive')}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                Work Assignment
              </h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Branch</Label>
                <Select
                  value={form.branchId ? String(form.branchId) : ''}
                  onValueChange={(val) => handleChange('branchId', Number(val))}
                  disabled={viewMode || !current}
                >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.branchName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <div className="flex gap-2">
                  <Select
                    value={form.department || ''}
                    onValueChange={(val) => handleChange('department', val)}
                    disabled={viewMode}
                  >
                    <SelectTrigger className="flex-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder={selectedBranch ? 'Select department' : 'Select a branch first'} />
                    </SelectTrigger>
                    <SelectContentWithSearch searchPlaceholder="Search departments...">
                      {departments.map(d => (
                        <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                      ))}
                    </SelectContentWithSearch>
                  </Select>
                  {!viewMode && (
                    <Dialog open={isAddDepartmentOpen} onOpenChange={setIsAddDepartmentOpen}>
                      <Button type="button" size="icon" variant="outline" className="border-primary-200 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 hover:border-primary-300" onClick={() => setIsAddDepartmentOpen(true)}>
                        <Plus className="h-4 w-4 text-primary-600" />
                      </Button>
                      <DialogContent className="border-primary-200">
                        <DialogHeader>
                          <DialogTitle className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Add New Department</DialogTitle>
                          <DialogDescription>Enter a new department name.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="newDepartment">Department Name</Label>
                            <Input id="newDepartment" value={newDepartment} onChange={(e) => setNewDepartment(e.target.value)} placeholder="e.g., Production" className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setIsAddDepartmentOpen(false)} className="border-primary-200 hover:bg-primary-50">Cancel</Button>
                          <Button type="button" onClick={async () => {
                            const value = newDepartment.trim();
                            if (!value) {
                              toast.error('Please enter department name');
                              return;
                            }
                            
                            // Check if department already exists
                            if (departments.some(d => d.name.toLowerCase() === value.toLowerCase())) {
                              toast.error('Department already exists');
                              return;
                            }
                            
                            try {
                              // Call backend API to create department
                              await departmentApiService.createDepartment({
                                name: value,
                                code: value, // Keep code same as name
                                isActive: true,
                                branchId: selectedBranch || undefined
                              });
                              
                              // Refresh department list
                              if (selectedBranch) {
                                const updatedDepartments = await departmentApiService.getActiveByBranch(selectedBranch);
                                setDepartments(updatedDepartments as Department[]);
                              }
                              
                              setNewDepartment('');
                              setIsAddDepartmentOpen(false);
                            } catch (error) {
                              // Error is already handled by the API service
                              // console.error('Failed to create department:', error);
                            }
                          }} className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg">Add Department</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Work Area</Label>
                <Select
                  value={form.workAreaId ? String(form.workAreaId) : ''}
                  onValueChange={(val) => {
                    const wa = workAreas.find(w => String(w.id) === val);
                    handleChange('workAreaId', Number(val));
                    handleChange('workArea', wa?.workAreaName || '');
                  }}
                  disabled={viewMode}
                >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder={selectedBranch ? 'Select work area' : 'Select a branch first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {workAreas.map(w => (
                      <SelectItem key={w.id} value={String(w.id)}>{w.workAreaName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reporting Manager</Label>
                <Select
                  value={form.reportingManagerUserId ? String(form.reportingManagerUserId) : ''}
                  onValueChange={(val) => {
                    const user = users.find(u => String(u.id) === val);
                    handleChange('reportingManagerUserId', Number(val));
                    handleChange('reportingManager', user?.fullName || user?.displayName || user?.username || '');
                  }}
                  disabled={viewMode}
                >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="Select reporting manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.displayName || u.fullName || `${u.firstName} ${u.lastName}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Shift</Label>
                <div className="flex gap-2">
                  <Select
                    value={form.shift || ''}
                    onValueChange={(val) => handleChange('shift', val)}
                    disabled={viewMode}
                  >
                    <SelectTrigger className="flex-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder="Select shift" />
                    </SelectTrigger>
                    <SelectContent>
                      {shifts.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!viewMode && (
                    <Dialog open={isAddShiftOpen} onOpenChange={setIsAddShiftOpen}>
                      <Button type="button" size="icon" variant="outline" className="border-primary-200 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 hover:border-primary-300" onClick={() => setIsAddShiftOpen(true)}>
                        <Plus className="h-4 w-4 text-primary-600" />
                      </Button>
                      <DialogContent className="border-primary-200">
                        <DialogHeader>
                          <DialogTitle className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Add New Shift</DialogTitle>
                          <DialogDescription>Enter a new shift name.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="newShift">Shift</Label>
                            <Input id="newShift" value={newShift} onChange={(e) => setNewShift(e.target.value)} placeholder="e.g., EVENING" className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setIsAddShiftOpen(false)} className="border-primary-200 hover:bg-primary-50">Cancel</Button>
                          <Button type="button" onClick={() => {
                            const value = newShift.trim();
                            if (!value) return;
                            if (!shifts.some(s => s.toLowerCase() === value.toLowerCase())) {
                              setShifts(prev => [...prev, value]);
                              setNewShift('');
                              setIsAddShiftOpen(false);
                              toast.success('Shift added');
                            } else {
                              toast.message('Shift already exists');
                            }
                          }} className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg">Add Shift</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Skill Level</Label>
                <div className="flex gap-2">
                  <Select
                    value={form.skillLevel || ''}
                    onValueChange={(val) => handleChange('skillLevel', val)}
                    disabled={viewMode}
                  >
                    <SelectTrigger className="flex-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder="Select skill level" />
                    </SelectTrigger>
                    <SelectContent>
                      {skillLevels.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!viewMode && (
                    <Dialog open={isAddSkillOpen} onOpenChange={setIsAddSkillOpen}>
                      <Button type="button" size="icon" variant="outline" className="border-primary-200 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 hover:border-primary-300" onClick={() => setIsAddSkillOpen(true)}>
                        <Plus className="h-4 w-4 text-primary-600" />
                      </Button>
                      <DialogContent className="border-primary-200">
                        <DialogHeader>
                          <DialogTitle className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Add Skill Level</DialogTitle>
                          <DialogDescription>Enter a new skill level.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="newSkill">Skill Level</Label>
                            <Input id="newSkill" value={newSkill} onChange={(e) => setNewSkill(e.target.value)} placeholder="e.g., Master" className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setIsAddSkillOpen(false)} className="border-primary-200 hover:bg-primary-50">Cancel</Button>
                          <Button type="button" onClick={() => {
                            const value = newSkill.trim();
                            if (!value) return;
                            if (!skillLevels.some(s => s.toLowerCase() === value.toLowerCase())) {
                              setSkillLevels(prev => [...prev, value]);
                              setNewSkill('');
                              setIsAddSkillOpen(false);
                              toast.success('Skill level added');
                            } else {
                              toast.message('Skill level already exists');
                            }
                          }} className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg">Add Skill Level</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </div>
          </div>

          {!viewMode && (
            <div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                disabled={loading}
              >
                {loading ? 'Saving...' : (current ? 'Update Worker' : 'Create Worker')}
              </Button>
            </div>
          )}

          {viewMode && (
            <div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Close
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}


